import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  ValidateDiscountCodeRequest,
  ValidateDiscountCodeResponse,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  CancelSubscriptionResponse,
  GetPlansResponse,
  GetSubscriptionResponse,
  GetAffiliateStatsResponse,
  CreateAffiliateRequest,
  PaginationParams,
  DateRangeParams,
  toQueryParams,
  CreatePaymentIntentResponse,
  CreatePaymentIntentRequest,
  CreateOneTimePaymentRequest,
  CreateOneTimePaymentResponse,
  ConfirmOneTimePaymentRequest,
  ConfirmOneTimePaymentResponse,
  GetEntitySetupPlanResponse,
  SeatSummary,
  ProrationMode,
} from "@/types/billing";

class BillingAPIService {
  // Plans and Pricing
  static async getPlans(countryCode?: string): Promise<GetPlansResponse> {
    const queryParams = countryCode ? { country_code: countryCode } : undefined;
    return ApiService.request<GetPlansResponse>(
      EndpointKeys.GET_BILLING_PLANS,
      { skipAuth: true, queryParams },
    );
  }

  static async getEntitySetupPlan(countryCode?: string): Promise<GetEntitySetupPlanResponse> {
    const queryParams = countryCode ? { country_code: countryCode } : undefined;
    return ApiService.request<GetEntitySetupPlanResponse>(
      EndpointKeys.GET_ENTITY_SETUP_PLAN,
      { queryParams },
    );
  }

  // Discount Codes
  static async validateDiscountCode(
    code: string,
    planName?: string
  ): Promise<ValidateDiscountCodeResponse> {
    const payload: ValidateDiscountCodeRequest = { code, planName };
    return ApiService.request<ValidateDiscountCodeResponse>(
      EndpointKeys.VALIDATE_DISCOUNT_CODE,
      {
        method: "POST",
        body: payload,
        skipAuth: true,
      },
      false,
    );
  }

  // Subscriptions
  static async createSubscription(
    data: CreateSubscriptionRequest,
  ): Promise<CreateSubscriptionResponse> {
    return ApiService.request<CreateSubscriptionResponse>(
      EndpointKeys.CREATE_SUBSCRIPTION,
      {
        method: "POST",
        body: data,
        skipAuth: true,
      },
      false,
    );
  }

  static async cancelSubscription(
    subscriptionId: number,
  ): Promise<CancelSubscriptionResponse> {
    return ApiService.request<CancelSubscriptionResponse>(
      EndpointKeys.CANCEL_SUBSCRIPTION,
      {
        method: "POST",
        body: { subscriptionId },
      },
    );
  }

  static async getSubscription(
    subscriptionId: number,
  ): Promise<GetSubscriptionResponse> {
    return ApiService.request<GetSubscriptionResponse>(
      EndpointKeys.GET_SUBSCRIPTION,
      {
        queryParams: { subscriptionId },
      },
    );
  }

  // One-time Payments
  static async createOneTimePayment(
    data: CreateOneTimePaymentRequest,
  ): Promise<CreateOneTimePaymentResponse> {
    return ApiService.request<CreateOneTimePaymentResponse>(
      EndpointKeys.CREATE_ONE_TIME_PAYMENT,
      {
        method: "POST",
        body: data,
      },
    );
  }

  static async confirmOneTimePayment(
    data: ConfirmOneTimePaymentRequest,
  ): Promise<ConfirmOneTimePaymentResponse> {
    return ApiService.request<ConfirmOneTimePaymentResponse>(
      EndpointKeys.CONFIRM_ONE_TIME_PAYMENT,
      {
        method: "POST",
        body: data,
      },
    );
  }

  // Payment Methods
  static async createSetupIntent(
    userEmail: string,
    planInfo?: {
      billingTierId?: number;
      planName?: string;
      discountCode?: string;
      affiliateCode?: string;
    }
  ): Promise<{ clientSecret: string }> {
    return ApiService.request<{ clientSecret: string }>(
      EndpointKeys.CREATE_SETUP_INTENT,
      {
        method: "POST",
        skipAuth: true,
        body: { 
          email: userEmail,
          billingTierId: planInfo?.billingTierId,
          planName: planInfo?.planName,
          discountCode: planInfo?.discountCode,
          affiliateCode: planInfo?.affiliateCode,
        },
      },
      false,
    );
  }

  static async updatePaymentMethod(
    subscriptionId: number,
    paymentMethodId: string,
  ): Promise<void> {
    return ApiService.request(EndpointKeys.UPDATE_PAYMENT_METHOD, {
      method: "POST",
      body: { subscriptionId, paymentMethodId },
    });
  }

  // Affiliate Program
  static async createAffiliate(data: CreateAffiliateRequest): Promise<void> {
    return ApiService.request(EndpointKeys.CREATE_AFFILIATE, {
      method: "POST",
      body: data,
    });
  }

  static async getAffiliateStats(): Promise<GetAffiliateStatsResponse> {
    return ApiService.request<GetAffiliateStatsResponse>(
      EndpointKeys.GET_AFFILIATE_STATS,
    );
  }

  static async createPaymentIntent(
    data: CreatePaymentIntentRequest,
  ): Promise<CreatePaymentIntentResponse> {
    return ApiService.request<CreatePaymentIntentResponse>(
      EndpointKeys.CREATE_PAYMENT_INTENT,
      {
        skipAuth: true,
        method: "POST",
        body: data,
      },
      false,
    );
  }

  static async attachPaymentMethod(paymentMethodId: string) {
    return ApiService.request<void>(
      EndpointKeys.ATTACH_PAYMENT_METHOD,
      {
        skipAuth: true,
        method: "POST",
        body: { paymentMethodId },
      },
      false,
    );
  }

  // Seat-based subscription APIs
  static async getSeatSummary(employerUserId?: number): Promise<SeatSummary> {
    // Backend expects userId for superuser context, otherwise derives employer from current user
    return ApiService.request<SeatSummary>(EndpointKeys.GET_SEAT_SUMMARY, {
      ...(employerUserId !== undefined ? { queryParams: { userId: employerUserId } } : {}),
    });
  }

  static async createSeatCheckout(
    employerUserId: number | undefined,
    seatsToAdd: number,
  ): Promise<{ checkoutUrl: string }> {
    return ApiService.request<{ checkoutUrl: string }>(
      EndpointKeys.CREATE_SEAT_CHECKOUT,
      {
        method: "POST",
        ...(employerUserId !== undefined ? { queryParams: { userId: employerUserId } } : {}),
        body: { seatsToAdd },
      },
    );
  }

  static async updateSeats(
    employerUserId: number | undefined,
    newQuantity: number,
    mode: ProrationMode,
  ): Promise<{ success: boolean }> {
    return ApiService.request<{ success: boolean }>(EndpointKeys.UPDATE_SEATS, {
      method: "POST",
      ...(employerUserId !== undefined ? { queryParams: { userId: employerUserId } } : {}),
      body: { newQuantity, mode },
    });
  }
}

export default BillingAPIService;
